// SPDX-License-Identifier: GPL-3.0-or-later

import { parseHash } from '../router';
import { getWalletState } from '../wallet';
import { el } from './dom';
import { getTheme, toggleTheme } from './theme';

export interface ShellOptions {
  showSettings: boolean;
  rootName?: string | undefined;
  /** Optional explicit active route key for highlighting nav links. */
  activeRoute?: string | undefined;
}

function renderWalletChip(): HTMLElement {
  const s = getWalletState();
  if (s.connection) {
    const label = s.budget
      ? `${s.budget.remaining}/${s.budget.limit}`
      : 'connected';
    return el(
      'a',
      {
        class: 'btn compact',
        href: '#/settings?focus=wallet',
        title: 'AI is on. Click to manage.',
        'aria-label': `AI ${label}`,
      },
      el('span', { class: 'dot offer', style: 'width: 7px; height: 7px; border-radius: 50%; display: inline-block;' }),
      'AI',
      el('span', { class: 'text-faint', style: 'margin-left: 0.25rem; font-size: 0.7rem;' }, label),
    );
  }
  return el(
    'a',
    {
      class: 'btn compact',
      href: '#/settings?focus=wallet',
      title: 'Turn on AI to draft documents and screen jobs.',
    },
    el('span', { class: 'dot saved', style: 'width: 7px; height: 7px; border-radius: 50%; display: inline-block; opacity: 0.6;' }),
    'Use AI',
  );
}

function renderThemeToggle(): HTMLElement {
  const theme = getTheme();
  return el(
    'button',
    {
      class: 'btn ghost icon-btn',
      title: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
      'aria-label': 'Toggle theme',
      onclick: () => {
        toggleTheme();
        const btn = document.querySelector('[data-theme-toggle]');
        if (btn) btn.textContent = getTheme() === 'dark' ? '☀' : '☾';
      },
      'data-theme-toggle': 'true',
    },
    theme === 'dark' ? '☀' : '☾',
  );
}

function renderRootChip(rootName: string): HTMLElement {
  const wrap = el(
    'span',
    { class: 'root-chip', title: `Workspace folder: ${rootName}` },
  );
  wrap.insertAdjacentHTML(
    'afterbegin',
    `<svg class="folder-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1.75 4.25h4l1.5 1.5h7v6a1 1 0 0 1-1 1H2.75a1 1 0 0 1-1-1v-7.5z"/></svg>`,
  );
  wrap.appendChild(document.createTextNode(rootName));
  return wrap;
}

function navLinks(activeRoute: string | undefined): HTMLElement {
  const cur = activeRoute ?? parseHash(location.hash).name;
  const link = (href: string, label: string, match: string): HTMLElement =>
    el('a', { href, class: cur === match ? 'active' : '' }, label);
  return el(
    'nav',
    { class: 'nav', 'aria-label': 'Primary' },
    link('#/', 'Dashboard', 'dashboard'),
    link('#/settings', 'Settings', 'settings'),
    link('#/privacy', 'Privacy', 'privacy'),
  );
}

export function renderShell(body: Node, opts: ShellOptions): HTMLElement {
  const showNav = Boolean(opts.rootName);
  const walletChip = opts.rootName ? renderWalletChip() : null;
  const rootChip = opts.rootName ? renderRootChip(opts.rootName) : null;

  return el(
    'div',
    { class: 'shell' },
    el(
      'header',
      { class: 'app-header' },
      el(
        'a',
        { class: 'logo', href: '#/', 'aria-label': 'Applify home' },
        el('img', {
          class: 'logo-mark',
          src: '/applify-icon.svg',
          alt: '',
          width: '28',
          height: '28',
        }),
        'Appli',
        el('span', { class: 'slash' }, '/'),
        'fy',
      ),
      showNav ? navLinks(opts.activeRoute) : null,
      el(
        'div',
        { class: 'header-actions' },
        rootChip,
        walletChip,
        renderThemeToggle(),
      ),
    ),
    body,
    el(
      'footer',
      { class: 'app-footer' },
      el('span', {}, 'Applify · runs entirely in your browser'),
      el(
        'span',
        { class: 'footer-right' },
        el('a', { href: '#/privacy' }, 'Privacy'),
        el(
          'a',
          { href: 'https://github.com/JeanHaiz/applify', target: '_blank', rel: 'noreferrer' },
          'GitHub',
        ),
        el('a', { href: '#/smoke' }, 'Browser check'),
      ),
    ),
  );
}
